import React, { useState, useMemo } from 'react';
import { 
  FileOutput, 
  Search, 
  Plus, 
  Calendar, 
  User, 
  ChevronRight, 
  ChevronDown,
  Printer
} from 'lucide-react';
import { useStore } from '../../hooks/useStore.js';
import { formatDateThai } from '../../utils/date.js';
import { generateStockOutReportHtml, triggerPrint } from '../../utils/print.js';

export function StockOutView({ onOpenStockOutModal }) {
  const stockOutMaster = useStore(s => s.stockOutMaster) || [];
  const stockOutDetails = useStore(s => s.stockOutDetails) || [];
  const inventory = useStore(s => s.inventory) || [];

  const [search, setSearch] = useState('');
  const [expandedMasterId, setExpandedMasterId] = useState(null);

  const filteredMasters = useMemo(() => {
    let list = stockOutMaster.filter(m => !m.deleted);

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(m => 
        (m.reason || '').toLowerCase().includes(q) ||
        (m.operator || m.operatorName || m.operator_name || '').toLowerCase().includes(q)
      );
    }

    return list.sort((a, b) => {
      const timeA = new Date(a.date || a.timestamp || a.createdAt || 0).getTime();
      const timeB = new Date(b.date || b.timestamp || b.createdAt || 0).getTime();
      return timeB - timeA;
    });
  }, [stockOutMaster, search]);

  const toggleExpand = (id) => {
    setExpandedMasterId(expandedMasterId === id ? null : id);
  };

  return (
    <div className="space-y-4 touch-scroll">
      {/* Top Bar */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาเหตุผลการจ่ายออก หรือชื่อผู้ดำเนินการ..."
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-300 bg-white text-sm text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
          />
        </div>

        <button
          onClick={() => onOpenStockOutModal()}
          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs shadow-xs transition-colors cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          <span>บันทึกการจ่ายสินค้าออก</span>
        </button>
      </div>

      {/* Stock Out List */}
      <div className="rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur-md shadow-xs overflow-hidden">
        {filteredMasters.length === 0 ? (
          <div className="p-12 text-center text-slate-600 font-medium text-sm">
            ไม่พบประวัติการจ่ายสินค้าออก
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredMasters.map((master) => {
              const details = stockOutDetails.filter(d => 
                !d.deleted && 
                (d.stockOutId === master.id || d.masterId === master.id || d.stock_out_id === master.id)
              );
              const isExpanded = expandedMasterId === master.id;

              return (
                <div key={master.id} className="transition-colors">
                  <div
                    onClick={() => toggleExpand(master.id)}
                    className="p-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50/70 select-none"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600 flex-shrink-0">
                        <FileOutput className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-bold text-slate-950 text-sm">
                          {master.reason || 'ไม่ระบุเหตุผล'}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-700 font-medium">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5 text-slate-500" />
                            <span>{formatDateThai(master.date || master.timestamp || master.createdAt)}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5 text-slate-500" />
                            <span>ผู้จ่ายออก: {master.operator || master.operatorName || master.operator_name || '-'}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const html = generateStockOutReportHtml({
                            master,
                            details,
                            inventoryItems: inventory,
                            printDate: new Date()
                          });
                          triggerPrint(html);
                        }}
                        title="พิมพ์ใบจ่ายสินค้า (PDF)"
                        className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
                      >
                        <Printer className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-xs font-bold text-slate-800 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg">
                        {details.length} รายการ
                      </span>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details Sub-table */}
                  {isExpanded && (
                    <div className="px-5 pb-4 pt-1 bg-slate-50/50 border-t border-slate-100 animate-in fade-in duration-100">
                      {details.length === 0 ? (
                        <div className="py-3 text-center text-slate-500 text-xs">
                          ไม่มีข้อมูลรายการย่อย
                        </div>
                      ) : (
                        <div className="table-touch-container">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead>
                              <tr className="border-b border-slate-200/80 text-slate-800 font-extrabold">
                                <th className="py-2">รายการพัสดุ</th>
                                <th className="py-2">ล็อต</th>
                                <th className="py-2 text-right">จำนวนที่จ่ายออก</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {details.map((d) => {
                                const invItem = inventory.find(i => i.id === (d.itemId || d.item_id));
                                const itemName = d.itemName || d.item_name || invItem?.name || 'พัสดุ';
                                const brand = d.brand || invItem?.brand || '';
                                const lot = d.lot || invItem?.lot || 'ล็อตหลัก';
                                const unit = d.unit || invItem?.unit || 'ชิ้น';
                                const qty = Number(d.quantity) || 0;

                                return (
                                  <tr key={d.id}>
                                    <td className="py-2 font-bold text-slate-900">
                                      {itemName} {brand ? `(${brand})` : ''}
                                    </td>
                                    <td className="py-2 text-slate-700 font-semibold">{lot}</td>
                                    <td className="py-2 text-right font-extrabold text-red-700 tabular-nums">
                                      {qty} {unit}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <div className="flex justify-end pt-3">
                            <button
                              onClick={() => {
                                const html = generateStockOutReportHtml({
                                  master,
                                  details,
                                  inventoryItems: inventory,
                                  printDate: new Date()
                                });
                                triggerPrint(html);
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold cursor-pointer transition-colors"
                            >
                              <Printer className="w-3.5 h-3.5" />
                              <span>พิมพ์ใบจ่ายสินค้า (Stock Out Voucher PDF)</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
